/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from "react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

import TabItem from "../TabItem/TabItem";
import Tab, { TabProps } from "../Tab";

export enum TabControls {
  always = "always",
  never = "never",
  auto = "auto",
}

export interface TabsProps {
  onChange?: (newValue: number) => void;
  value?: number;
  disabled?: boolean;

  /**
   * When should controls show. auto will show when the tabs expand past the screen. default is auto.
   */
  controls?: TabControls | `${TabControls}` | boolean;
}

const Tabs: React.FC<React.PropsWithChildren<TabsProps>> = ({
  value,
  onChange,
  disabled = false,
  children,
  controls = TabControls.auto,
}) => {
  // If no value (undefined or null) is provided, we control this but will still notify the parent via onChange.
  const [controlledValue, setControlledValue] = React.useState<number | undefined>(0);
  const [showControls, setShowControls] = React.useState(controls === TabControls.always);
  const [width, setWidth] = React.useState(0);
  const childrenRef = React.useRef<HTMLDivElement[]>([]);
  const ref = React.useRef<HTMLDivElement>(null);

  // Ensure all children are a <Tab /> component
  const childrenArray = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<TabProps>[];
  if (!childrenArray.every((child) => child.type === Tab)) {
    console.warn("<Tabs /> only accepts <Tab /> components as children. Anything else is undefined behavior.");
  }

  useEffect(() => {
    const setEqualWidth = () => {
      let maxWidth = 0;

      // Reset widths
      for (let i = 0; i < childrenRef.current.length; i += 1) {
        const child = childrenRef.current[i];
        if (child) {
          child.style.minWidth = "auto";
        }
      }

      for (let i = 0; i < childrenRef.current.length; i += 1) {
        const child = childrenRef.current[i];
        if (child) {
          const width = child.clientWidth;
          maxWidth = Math.max(width, maxWidth);
        }
      }

      for (let i = 0; i < childrenRef.current.length; i += 1) {
        const child = childrenRef.current[i];
        if (child) {
          child.style.minWidth = `${maxWidth}px`;
        }
      }

      // For line
      setWidth(maxWidth);
    };

    const checkControls = () => {
      // Allow a margin of 2 due to calculcation/floating point errors.
      const difference = ref.current ? (ref.current.scrollWidth || 0) - (ref.current.clientWidth || 0) : 0;
      if (showControls) {
        if (controls !== TabControls.always && difference <= 2) {
          // Do we still need to show controls?
          setShowControls(false);
        }
      } else if (controls === TabControls.always) {
        setShowControls(true);
      } else if (controls === TabControls.auto && difference > 2) {
        setShowControls(true);
      }
    };

    window.addEventListener("resize", () => {
      setEqualWidth();
      checkControls();
    });

    setEqualWidth();
    checkControls();

    return () => {
      window.removeEventListener("resize", setEqualWidth);
    };
  }, [controls, showControls]);

  const leftMargin = showControls ? 20 : 0;
  const rightMargin = showControls ? 20 : 0;

  return (
    <div>
      <div className="relative">
        {showControls && (
          <button
            onClick={() => {
              if (ref.current) {
                ref.current.scrollLeft -= width;
              }
            }}
            type="button"
            className="absolute py-2 h-full flex justify-center items-center hover:bg-bg-components-hover active:bg-bg-components-pressed"
          >
            <IconChevronLeft width={20} height={20} />
          </button>
        )}
        {showControls && (
          <button
            onClick={() => {
              if (ref.current) {
                ref.current.scrollLeft += width;
              }
            }}
            type="button"
            className="right-0 absolute py-2 h-full flex justify-center items-center hover:bg-bg-components-hover active:bg-bg-components-pressed"
          >
            <IconChevronRight width={20} height={20} />
          </button>
        )}
        <div
          className="flex-1 relative border-b border-bg-border-base overflow-x-hidden"
          ref={ref}
          style={{
            gridTemplateColumns: `repeat(${childrenArray.length}, 1fr)`,
            gridTemplateRows: "auto",
            display: "grid", // Add this line to enable grid layout
            marginLeft: leftMargin,
            marginRight: rightMargin,
            scrollBehavior: "smooth",
          }}
        >
          <div
            id="line"
            className="h-0.5 bg-text-interactive-base absolute bottom-0"
            style={{
              left: `${(controlledValue ?? value) * width}px`,
              width,
              transition: "left 0.2s",
            }}
          />
          {childrenArray.map((child, i) => {
            const {
              selected, value: tabValue, disabled: tabDisabled, value, label, ...rest
            } = child.props;

            const isSelected = controlledValue !== undefined
              ? controlledValue === i
              : value === i;

            return (
              <div
                ref={(el) => {
                  childrenRef.current[i] = el;
                }}
                key={i}
              >
                <TabItem
                  value={value ?? i}
                  onClick={() => {
                    if (disabled || tabDisabled) return;
                    setControlledValue(i);
                    onChange?.(i);

                    // Scroll on click if partially invisible
                    if (ref.current) {
                      const left = childrenRef.current[i].offsetLeft;
                      const right = left + childrenRef.current[i].clientWidth;
                      if (left < ref.current.scrollLeft) {
                        ref.current.scrollLeft = left;
                      } else if (right > ref.current.scrollLeft + ref.current.clientWidth) {
                        ref.current.scrollLeft = right - ref.current.clientWidth;
                      }
                    }
                  }}
                  key={i}
                  selected={isSelected}
                  disabled={disabled || tabDisabled}
                  label={label}
                  {...rest}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Clone children and only show active child */}
      {childrenArray.map((child, i) => {
        const { value: tabValue, ...rest } = child.props;
        const isSelected = controlledValue !== undefined
          ? controlledValue === i
          : value === i;

        return (
          <div key={i} className={`py-2 ${isSelected ? "" : "hidden"}`}>
            {React.cloneElement(child, {
              selected: isSelected,
              value: tabValue ?? i,
              ...rest,
            })}
          </div>
        );
      })}
    </div>
  );
};

export default Tabs;
